import { useCallback, useEffect, useState } from "react";
import { Copy, ExternalLink, Loader2, Lock, LockOpen, Plus, RotateCcw, Sparkles, Trash2, Vote, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InfoNote, SectionCard, Segmented } from "@/components/common/Cards";
import { Sel } from "@/components/common/Sel";
import { fmtMeetingDate, voteUrl } from "@/components/meeting/meetUtils";

const emptyForm = () => ({ question: "", options: ["", ""] });

export function PollsPanel({ type }) {
  const [polls, setPolls] = useState(null);
  const [mode, setMode] = useState("ai");
  const [topic, setTopic] = useState("");
  const [nOpt, setNOpt] = useState("4");
  const [form, setForm] = useState(emptyForm());
  const [genBusy, setGenBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/meeting/polls", { params: { type } });
      setPolls(data);
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat voting"));
      setPolls([]);
    }
  }, [type]);
  useEffect(() => {
    setPolls(null);
    load();
  }, [load]);
  // live results refresh
  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const generate = async () => {
    if (topic.trim().length < 3) return toast.error("Tulis topik voting dulu.");
    setGenBusy(true);
    try {
      const { data } = await api.post("/meeting/polls/generate", { type, topic, n_options: Number(nOpt) });
      setForm({ question: data.question, options: data.options });
      toast.success("Draf voting dari AI siap — periksa lalu publikasikan.");
    } catch (e) {
      toast.error(errMsg(e, "AI gagal membuat voting"));
    } finally {
      setGenBusy(false);
    }
  };

  const publish = async () => {
    const opts = form.options.map((o) => o.trim()).filter(Boolean);
    if (form.question.trim().length < 3) return toast.error("Isi pertanyaan voting.");
    if (opts.length < 2) return toast.error("Minimal 2 pilihan jawaban.");
    setSaving(true);
    try {
      const { data } = await api.post("/meeting/polls", { type, question: form.question, options: opts });
      setForm(emptyForm());
      setTopic("");
      await load();
      await copyLink(data.public_id, true);
    } catch (e) {
      toast.error(errMsg(e, "Gagal membuat voting"));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async (pid, fresh = false) => {
    const url = voteUrl(pid);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(fresh ? "Voting dipublikasikan, link disalin!" : "Link voting disalin");
    } catch (_) {
      toast.info(url);
    }
  };
  const setStatus = async (p, status) => {
    try {
      await api.put(`/meeting/polls/${p.id}`, { status });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reset = async (p) => {
    try {
      await api.post(`/meeting/polls/${p.id}/reset`);
      toast.success("Suara direset");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    const p = del;
    setDel(null);
    try {
      await api.delete(`/meeting/polls/${p.id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setOpt = (i, v) => setForm((f) => ({ ...f, options: f.options.map((o, j) => (j === i ? v : o)) }));

  return (
    <div className="grid xl:grid-cols-[400px_1fr] gap-5 items-start">
      <SectionCard title="Buat voting" eyebrow="Pilihan ganda" testid="poll-create">
        <div className="space-y-3">
          <Segmented value={mode} onChange={setMode} size="sm" testid="poll-mode" options={[{ value: "ai", label: "Dibuat AI" }, { value: "manual", label: "Manual" }]} />
          {mode === "ai" && (
            <div className="space-y-2 rounded-lg border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] p-3">
              <Label className="text-xs text-muted-foreground">Topik voting</Label>
              <Textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={2} placeholder="mis. prioritas proyek kuartal depan" className="bg-[hsl(220_12%_8%)] border-[hsl(220_10%_18%)] text-sm" data-testid="poll-ai-topic" />
              <div className="flex gap-2">
                <div className="w-32"><Sel value={nOpt} onChange={setNOpt} options={[2, 3, 4, 5, 6].map((n) => ({ value: String(n), label: `${n} pilihan` }))} testid="poll-ai-n" /></div>
                <Button onClick={generate} disabled={genBusy} className="flex-1" data-testid="poll-ai-generate">
                  {genBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Buat dengan AI
                </Button>
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs text-muted-foreground">Pertanyaan</Label>
            <Textarea value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} rows={2} className="mt-1 bg-[hsl(220_12%_8%)] border-[hsl(220_10%_18%)] text-sm" data-testid="poll-question" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Pilihan jawaban</Label>
            {form.options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <span className="h-10 w-8 flex items-center justify-center text-xs text-primary num">{String.fromCharCode(65 + i)}</span>
                <Input value={o} onChange={(e) => setOpt(i, e.target.value)} className="h-10 bg-[hsl(220_12%_8%)]" maxLength={200} data-testid={`poll-option-${i}`} />
                {form.options.length > 2 && (
                  <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, options: form.options.filter((_, j) => j !== i) })} aria-label="Hapus pilihan" data-testid={`poll-option-remove-${i}`}><X className="h-4 w-4" /></Button>
                )}
              </div>
            ))}
            {form.options.length < 8 && (
              <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, options: [...form.options, ""] })} data-testid="poll-option-add"><Plus className="h-3.5 w-3.5 mr-1.5" /> Tambah pilihan</Button>
            )}
          </div>
          <Button onClick={publish} disabled={saving} className="w-full h-11 press" data-testid="poll-publish">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Vote className="h-4 w-4 mr-2" />} Publikasikan & salin link
          </Button>
          <InfoNote>Siapa pun yang punya link bisa memilih tanpa login (anonim, 1 suara per perangkat) dan melihat hasil setelah memilih.</InfoNote>
        </div>
      </SectionCard>

      <div className="space-y-4 min-w-0" data-testid="poll-list">
        {polls === null && <div className="h-40 rounded-xl border border-border bg-card animate-pulse" />}
        {polls?.length === 0 && (
          <SectionCard testid="poll-empty"><p className="text-sm text-muted-foreground">Belum ada voting untuk meeting ini.</p></SectionCard>
        )}
        {(polls || []).map((p) => {
          const total = p.results?.total || 0;
          const max = Math.max(0, ...Object.values(p.results?.counts || {}));
          return (
            <div key={p.id} className="rounded-xl border border-border bg-card p-5 elev-1" data-testid={`poll-${p.id}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] text-muted-foreground">{fmtMeetingDate(p.date)} · dibuat {fmtDate(p.created_at)}</div>
                  <h3 className="text-base font-semibold mt-1">{p.question}</h3>
                </div>
                <Badge variant="outline" className={`text-[10px] ${p.status === "open" ? "border-[hsl(160_40%_30%)] text-[hsl(160_55%_60%)]" : "border-border text-muted-foreground"}`} data-testid={`poll-status-${p.id}`}>
                  {p.status === "open" ? "Dibuka" : "Ditutup"}
                </Badge>
                <span className="text-xs num text-muted-foreground" data-testid={`poll-total-${p.id}`}>{total} suara</span>
              </div>
              <div className="space-y-2 mt-4">
                {p.options.map((o, i) => {
                  const n = p.results?.counts?.[o.id] || 0;
                  const pct = total ? Math.round((n / total) * 100) : 0;
                  const lead = n > 0 && n === max;
                  return (
                    <div key={o.id} data-testid={`poll-${p.id}-opt-${i}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className={lead ? "text-primary" : ""}><span className="num text-xs text-muted-foreground mr-2">{String.fromCharCode(65 + i)}</span>{o.text}</span>
                        <span className="num text-xs text-muted-foreground">{n} · {pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-[hsl(220_10%_14%)] mt-1 overflow-hidden">
                        <div className={`h-full rounded-full transition-[width] duration-500 ${lead ? "bg-primary" : "bg-[hsl(43_35%_38%)]"}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Input readOnly value={voteUrl(p.public_id)} onFocus={(e) => e.target.select()} className="h-9 flex-1 min-w-[220px] bg-[hsl(220_12%_8%)] text-xs num" data-testid={`poll-link-${p.id}`} />
                <Button size="sm" onClick={() => copyLink(p.public_id)} data-testid={`poll-copy-${p.id}`}><Copy className="h-3.5 w-3.5 mr-1.5" /> Salin link</Button>
                <Button size="sm" variant="outline" asChild><a href={voteUrl(p.public_id)} target="_blank" rel="noreferrer" data-testid={`poll-open-${p.id}`}><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Buka</a></Button>
                {p.status === "open" ? (
                  <Button size="sm" variant="ghost" onClick={() => setStatus(p, "closed")} data-testid={`poll-close-${p.id}`}><Lock className="h-3.5 w-3.5 mr-1.5" /> Tutup</Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setStatus(p, "open")} data-testid={`poll-reopen-${p.id}`}><LockOpen className="h-3.5 w-3.5 mr-1.5" /> Buka lagi</Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => reset(p)} disabled={!total} data-testid={`poll-reset-${p.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset</Button>
                <Button size="sm" variant="ghost" onClick={() => setDel(p)} className="text-muted-foreground hover:text-[hsl(0_72%_66%)]" data-testid={`poll-delete-${p.id}`} aria-label="Hapus voting"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus voting ini?</AlertDialogTitle>
            <AlertDialogDescription>Link publik tidak bisa dibuka lagi dan semua suara dihapus.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="poll-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
